import '../App.css';
import React, { Component } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { embedComponent } from '../embed';
import { minMaxValidator } from '../utils/validators/index';
import Calculator from '../components/Calculator/index';
import CalculatorGroup from '../components/CalculatorGroup';
import CalculatorResults from '../components/CalculatorResults/index';
import CalculatorResult from '../components/CalculatorResult/index';
import CurrencyField from '../components/CurrencyField';
import Field from '../components/Field';
import formatCurrency from '../utils/formatters/formatCurrency';
import formatPercentage from '../utils/formatters/formatPercentage';
import PercentageField from '../components/PercentageField/index';
import CarComparisonGroup from '../components/CarComparisonGroup';
import numberWithLabelChoices from '../utils/forms/numberWithLabelChoices';

const popoverMessages = {
  totalMonthlyIncome: {
    title: 'Total Monthly Income',
    body: [
      'Enter your monthly gross income before taxes, 401k deductions, etc. Divide your total annual salary (or total earnings) by 12.'
    ]
  },
  maximumCarPayment: {
    title: 'Maximum Car Payment',
    body: [
        'You can afford this monthly payment.'
    ]
  },
  totalMonthlyCarCosts : {
    title: 'Total Monthly Car Costs',
    body: [
        'Your total monthly car costs should not exceed 15% of your gross monthly income.'
    ]
  },
  recommendedCarPayment: {
    title: 'Recommended Car Payment',
    body: [
        'You can override this number if you think you can afford to pay more.'
    ]
  },
  loanTerm: {
    title: 'Loan Term',
    body: [
        'Number of months it will take to pay off the loan.'
    ]
  },
  purchasePrice: {
    title: 'Purchase Price',
    body: [
      'You can afford to pay this amount for a car.'
    ]
  },
  priceOfCar: {
    title: 'Price of Car',
    body: [
        'Enter the total sales price, but do not include registration, delivery costs, or other costs that are due up front.'
    ]
  },
  monthlyPayment: {
    title: 'Monthly Payment',
    body: [
        'Your monthly car payment (does not include insurance or maintenance).'
    ]
  }
};

// Create select input options for dropdown.
const loanTermMonthValues = [36, 48, 60, 72, 84, 96]
const loanTermOptions = numberWithLabelChoices(loanTermMonthValues, ' months')

class BuyACarCalculator extends Component {
  constructor (props) {
    super(props);

    this.state = BuyACarCalculator.getInitialState(1);

    this.clearState = this.clearState.bind(this);
    this.onValidation = this.onValidation.bind(this);
    this.getCalculatorValues = this.getCalculatorValues.bind(this);
    this.setCalculatorValues = this.setCalculatorValues.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
    this.handleComparisonChange = this.handleComparisonChange.bind(this);
  }

  static getInitialState(activeTab) {
    return {
      activeTab: activeTab,
      totalMonthlyIncome: 0,
      maximumCarPayment: 0,
      avgInsurance: 133,
      minimumInsurance: false,
      avgGas: 164,
      electricVehicle: false,
      totalMonthlyCarCosts: 0,
      recommendedCarPayment: 0,
      loanTerm: 60,
      interestRate: 5,
      purchasePrice: 0,
      comparisonCar1: { priceOfCar: 0, loanTerm: 60, interestRate: 5, monthlyPayment: 0 },
      comparisonCar2: { priceOfCar: 0, loanTerm: 60, interestRate: 5, monthlyPayment: 0 },
      comparisonCar3: { priceOfCar: 0, loanTerm: 60, interestRate: 5, monthlyPayment: 0 },
      fieldErrors: {},
    }
  }

  componentDidMount() {
    document.addEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.addEventListener('getCalculatorValuesBuyACarCalculator', this.getCalculatorValues, true);
    document.addEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  getCalculatorValues (event) {
    this.removeEventListeners();

    // Build newObj off state and exclude fieldErrors
    let newObj = {};
    Object.keys(BuyACarCalculator.getInitialState(1)).forEach(key => {
      if (key !== 'fieldErrors' && key !== 'compareView' && key !== 'activeTab') {
          newObj[key] = this.state[key];
      }
    });

    document.dispatchEvent(new CustomEvent('receiveCalculatorStateBuyACarCalculator', { detail: newObj}));
  }

  setCalculatorValues (event) {
    this.handleChange(event.detail.value, event.detail.name);
  }

  removeEventListeners () {
    document.removeEventListener('getCalculatorValuesBuyACarCalculator', this.getCalculatorValues, true);
    document.removeEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.removeEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value });
  }

  handleComparisonChange (value, object, fieldName) {
    this.setState(prevState => ({
      [object]: {
        ...prevState[object],
        [fieldName]: value
      }
    }))
  }

  clearState () {
    const { activeTab } = this.state;

    this.setState(BuyACarCalculator.getInitialState(activeTab))
  }

  toggleCheckbox (fieldName) {
    const { minimumInsurance, electricVehicle } = this.state;

    switch(fieldName) {
      case 'minimumInsurance':
        if (minimumInsurance === false) {
          this.setState({ avgInsurance: 78 });
        }
        this.setState( { minimumInsurance: !minimumInsurance });
        break;
      case 'electricVehicle':
        if (electricVehicle === false) {
          // 0.01 is used as setting to 0 is treated as 'empty' triggering the validation message display.
          this.setState({ avgGas: 0.01 });
        }
        this.setState( { electricVehicle: !electricVehicle });
        break;
      default:
        break;
    }
  }

  handleTabSelect(activeTab) {
    this.setState({ activeTab })
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors)
        delete newErrors[name]
        this.setState({ fieldErrors: newErrors })
      }
    }
  }

  /**
   * Formula: totalMonthlyIncome * 15%
   */
  calculateTotalMonthlyCarCosts () {
    const { totalMonthlyIncome } = this.state;

    return Math.floor(totalMonthlyIncome * 0.15);
  }

  /**
   * Formula: totalMonthlyCarCosts - avgGas - avgInsurance
   */
  calculateMaximumMonthlyCarPayment () {
    const { avgInsurance, avgGas } = this.state;

    let totalMonthlyCosts = this.calculateTotalMonthlyCarCosts();

    return (totalMonthlyCosts > 0) ? Math.floor(totalMonthlyCosts - avgGas - avgInsurance) : 0;
  }

  /**
   * Formula: monthlyPayment * ((1+monthlyInterest^loanTerm - 1) / (1+monthlyInterest^loanTerm * loanTerm))
   */
  calculatePurchasePrice () {
    const { loanTerm, interestRate, recommendedCarPayment } = this.state;

    if (interestRate <= 0) {
      return 0;
    }

    let monthlyPayment = (recommendedCarPayment > 0) ? recommendedCarPayment : this.calculateMaximumMonthlyCarPayment();
    let monthlyInterest = ((interestRate / 100) / 12);

    let bottom1 = Math.pow(1+monthlyInterest, loanTerm) - 1;
    let bottom2 = Math.pow(1+monthlyInterest, loanTerm) * monthlyInterest;
    let fullBottom = bottom1 / bottom2;

    return Math.ceil(fullBottom * monthlyPayment);
  }

  /**
   * Formula: monthlyPayment / ((1+monthlyInterest^loanTerm - 1) / (1+monthlyInterest^loanTerm * loanTerm))
   */
  calculatePayment (loanAmount, loanTerm, interestRate) {
    if (interestRate <= 0) {
      return 0;
    }

    let monthlyInterest = ((interestRate / 100) / 12);
    let bottom1 = Math.pow(1+monthlyInterest, loanTerm) - 1;
    let bottom2 = Math.pow(1+monthlyInterest, loanTerm) * monthlyInterest;
    let fullBottom = bottom1 / bottom2;

    return Math.ceil(loanAmount / fullBottom);
  }

  render () {
    const {
      activeTab,
      totalMonthlyIncome,
      maximumCarPayment,
      avgInsurance,
      minimumInsurance,
      avgGas,
      electricVehicle,
      totalMonthlyCarCosts,
      recommendedCarPayment,
      loanTerm,
      interestRate,
      comparisonCar1,
      comparisonCar2,
      comparisonCar3
    } = this.state;

    const calculatedMaximumCarPayment = this.calculateMaximumMonthlyCarPayment();
    const calculatedTotalMonthlyCarCosts = this.calculateTotalMonthlyCarCosts();
    const calculatedPurchasePrice = this.calculatePurchasePrice();

    return (
      <Calculator title="Buy a Car Calculator">
        <div className="tab-buttons">
          <Button className={activeTab === 1 ? 'active' : ''} onClick={() => this.handleTabSelect(1)}>What can I afford?</Button>
          <Button className={activeTab === 2 ? 'active' : ''} onClick={() => this.handleTabSelect(2)}>Car comparison</Button>
        </div>
        <div className="calculator-body">
          {activeTab === 1 ? (
            <div>
              <CalculatorGroup
                label='What can I afford?'
                subLabel='The first step is to figure out what you can really afford. You might end up spending less (or more), but you should first calculate the maximum you should be spending.'
              />

              <Form>
                <CurrencyField
                  name="totalMonthlyIncome"
                  label="Total monthly income"
                  value={totalMonthlyIncome}
                  popover={popoverMessages.totalMonthlyIncome}
                  onChange={(e, value) => this.handleChange(value, 'totalMonthlyIncome') }
                />

                <CurrencyField
                  name="maximumCarPayment"
                  label="Maximum car payment"
                  value={maximumCarPayment}
                  calculatedValue={calculatedMaximumCarPayment}
                  popover={popoverMessages.maximumCarPayment}
                  onChange={(e, value) => this.handleChange(value, 'maximumCarPayment') }
                  onValidation={this.onValidation}
                  disabled={true}
                />

                <CurrencyField
                  name="avgInsurance"
                  label="Insurance"
                  value={avgInsurance}
                  onChange={(e, value) => this.handleChange(value, 'avgInsurance') }
                  validator={minMaxValidator(78, 100000, formatCurrency)}
                  onValidation={this.onValidation}
                />
                <div className="child-checkbox">
                  <div className="sub-label" onClick={() => this.toggleCheckbox('minimumInsurance')}>
                    <input
                      type="checkbox"
                      checked={minimumInsurance}
                    />
                    Minimum Insurance
                  </div>
                </div>

                <CurrencyField
                  name="avgGas"
                  label="Gas"
                  value={avgGas}
                  onChange={(e, value) => this.handleChange(value, 'avgGas') }
                  validator={minMaxValidator(0, 100000, formatCurrency)}
                  onValidation={this.onValidation}
                />
                <div className="child-checkbox">
                  <div className="sub-label" onClick={() => this.toggleCheckbox('electricVehicle')}>
                    <input
                      type="checkbox"
                      checked={electricVehicle}
                    />
                    Electric vehicle
                  </div>
                </div>

                <CurrencyField
                  name="totalMonthlyCarCosts"
                  label="Car costs you can afford per month"
                  value={totalMonthlyCarCosts}
                  calculatedValue={calculatedTotalMonthlyCarCosts}
                  popover={popoverMessages.totalMonthlyCarCosts}
                  onChange={(e, value) => this.handleChange(value, 'totalMonthlyCarCosts') }
                  onValidation={this.onValidation}
                  disabled={true}
                />

                <CalculatorGroup
                  label='What can I buy within my budget?'
                  subLabel='Knowing what you can afford to pay each month allows you to calculate the maximum purchase price you can afford. The standard term used to be three years, but now It is more typical for a car loan to be over 4 or 5 years (48 months or 60 months).'
                />

                <CurrencyField
                  name="recommendedCarPayment"
                  label="Recommended car payment"
                  value={recommendedCarPayment}
                  calculatedValue={calculatedMaximumCarPayment}
                  popover={popoverMessages.recommendedCarPayment}
                  onChange={(e, value) => this.handleChange(value, 'recommendedCarPayment') }
                  validator={minMaxValidator(100, 100000, formatCurrency)}
                  onValidation={this.onValidation}
                />

                <Field
                  type="select"
                  name="loanTerm"
                  label="Loan term (in months)"
                  value={loanTerm}
                  choices={loanTermOptions}
                  onChange={(e, value) => this.handleChange(value, 'loanTerm') }
                  popover={popoverMessages.loanTerm}
                />

                <PercentageField
                  name="interestRate"
                  label="Interest rate"
                  value={interestRate}
                  onChange={(e, value) => this.handleChange(value, 'interestRate') }
                  validator={minMaxValidator(0.01, 99.99, formatPercentage)}
                  onValidation={this.onValidation}
                />
              </Form>
              <CalculatorResults>
                <CalculatorResult
                  label='Purchase price'
                  popover={popoverMessages.purchasePrice}
                  value={formatCurrency(calculatedPurchasePrice)}
                />
              </CalculatorResults>
              <Row>
                <Col xs={12} className="calculator-buttons">
                  <Button bsStyle="calculator-clear" onClick={this.clearState}>Clear All</Button>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              <CarComparisonGroup
                label="What about this car?"
                subLabel="Maybe you have a specific car in mind. Enter the price of the car and see what your payments would be."
                comparisonCar1={comparisonCar1}
                comparisonCar2={comparisonCar2}
                comparisonCar3={comparisonCar3}
                loanTermOptions={loanTermOptions}
                handleComparisonChange={this.handleComparisonChange}
                calculatePayment={this.calculatePayment}
                onValidation={this.onValidation}
              />
              <Row>
                <Col xs={12} className="calculator-buttons">
                  <Button bsStyle="calculator-clear" onClick={this.clearState}>Clear All</Button>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Calculator>
    )
  }
}

embedComponent(BuyACarCalculator, 'tgm-buy-a-car-calc', 'tgm-buy-a-car-calc-embed')

export default BuyACarCalculator



// WEBPACK FOOTER //
// ./src/calculators/BuyACarCalculator.js